/**
 * @typedef Country
 *
 * @property {string} country_name
 * @property {string} country_handle
 * @property {string} store_handle
 */

/**
 * @typedef UserLocation
 *
 * @property {{
 *  country: {
 *    handle: string;
 *    name: string;
 *  };
 *  country_name: string;
 * }} detected_values
 */

/**
 * @typedef Store
 *
 * @property {string} title
 * @property {string} handle
 * @property {string} url
 * @property {boolean} isCurrent
 * @property {boolean} isEuStore
 * @property {boolean} isRestOfWorld
 * @property {string} flagCode
 * @property {string} [icon]
 * @property {string} currency
 */

class StoreSelector extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['close', 'form', 'select']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els.select.element, 'change', () =>
      this.handleSelectChange(),
    )

    this.listeners.add(this.els.close.element, 'click', () => {
      this.preferredStore = this.currentStore.handle
      this.hasMadeDecision = this.currentStore.handle
    })

    this.listeners.add(this.els.form.element, 'submit', () => {
      this.hasMadeDecision = this.selectedStore.handle
    })

    this.initialise()
  }

  async initialise () {
    this.setPreferredStoreFromUrl()

    const isPreferredStore = await this.isEquivalentToCurrentStore(
      this.preferredStore,
    )
    if (isPreferredStore) {
      return
    }

    const recommendedStore = await this.getRecommendedStoreForUsersCountry()
    this.updateSelect(recommendedStore.handle)

    if (
      recommendedStore.handle !== this.currentStore.handle &&
      !this.hasMadeDecision
    ) {
      this.requestModalOpen()
    }
  }

  /** @param {string} value */
  updateSelect (value) {
    const select = this.els.select.element
    select.value = value
    /** @note Change event must be fired manually to ensure flag icon is updated */
    select.dispatchEvent(new Event('change'))
  }

  setPreferredStoreFromUrl () {
    const store = new URL(window.location).searchParams.get('store')

    if (store) {
      this.preferredStore = store
    }
  }

  handleSelectChange () {
    this.updateFormAction()
    this.updateStoreIcon()
    this.updateBenefits()
  }

  updateFormAction () {
    const url = new URL(this.selectedStore.url)
    url.pathname = window.location.pathname || ''

    this.els.form.element.setAttribute('action', url.toString())
  }

  async updateStoreIcon () {
    const { flagCode, icon } = this.selectedStore
    const flagHtml = icon ?? (await this.getFlag(flagCode))

    if (!icon) {
      this.els['store-icon'].element.innerHTML = flagHtml
    } else {
      this.els[
        'store-icon'
      ].element.innerHTML = `<img class="sec-StoreSelector_StoreIconImage" src="${icon}" alt="" />`
    }
  }

  /** @param {string} countryCode */
  async getFlag (countryCode) {
    const flagCode = countryCode === 'uk' ? 'gb' : countryCode
    const response = await fetch(
      `https://raw.githubusercontent.com/lipis/flag-icons/main/flags/1x1/${flagCode}.svg`,
    )

    return response.text()
  }

  updateBenefits () {
    if (!this.els.benefits) {
      return
    }

    this.els.benefits.elements.forEach(panel => {
      const isSelectedStore = this.selectedStore.handle === panel.dataset.handle
      panel.setAttribute('aria-hidden', !isSelectedStore)
    })
  }

  async getRecommendedStoreForUsersCountry () {
    const [location, countries] = await Promise.all([
      this.getLocationData(),
      this.getCountriesData(),
    ])

    const usersCountry = countries.find(
      country => country.country_name === location.detected_values.country_name,
    )

    if (!usersCountry) {
      return this.restOfWorldStore
    }

    const recommendedStore = countries.find(
      country => country.country_handle === usersCountry.country_handle,
    )

    if (!recommendedStore) {
      return this.currentStore
    }

    return (
      this.stores.find(
        store => store.title === recommendedStore.country_name,
      ) ||
      this.stores.find(store => store.handle === recommendedStore.store_handle)
    )
  }

  /** @returns {Promise<UserLocation>} */
  async getLocationData () {
    return fetch('/browsing_context_suggestions.json').then(response =>
      response.json(),
    )
  }

  requestModalOpen () {
    this.dispatchEvent(
      new CustomEvent('site-modal:open', {
        bubbles: true,
        detail: { id: 'store-selector' },
      }),
    )
  }

  /** @returns {Promise<Country[]>} */
  async getCountriesData () {
    return fetch(this.dataset.storeCountriesUrl).then(response =>
      response.json(),
    )
  }

  /** @param {string} countryCode */
  async getIsEuStore (countryCode) {
    const store = this.stores.find(store => store.handle === countryCode)

    if (store) {
      return store.isEuStore
    }

    const countries = await this.getCountriesData()

    const country = countries.find(
      country => country.country_handle === countryCode,
    )

    if (!country) {
      return false
    }

    return country.store_handle === 'EU'
  }

  /** @param {string | null} countryCode */
  async isEquivalentToCurrentStore (countryCode) {
    if (!countryCode) {
      return false
    }

    if (countryCode === this.currentStore.handle) {
      return true
    }

    if (this.currentStore.isEuStore) {
      return this.getIsEuStore(countryCode)
    }

    return this.currentStore.url === this.restOfWorldStore.url
  }

  /** @returns {Store[]} */
  get stores () {
    return JSON.parse(this.dataset.stores)
  }

  get currentStore () {
    return this.stores.find(store => store.isCurrent)
  }

  get hasMadeDecision () {
    return window.localStorage.getItem('store-selector:selected')
  }

  set hasMadeDecision (selectedStore) {
    return window.localStorage.setItem('store-selector:selected', selectedStore)
  }

  get preferredStore () {
    return window.localStorage.getItem('store-selector:store')
  }

  set preferredStore (preferredStore) {
    return window.localStorage.setItem('store-selector:store', preferredStore)
  }

  get selectedStore () {
    return this.stores.find(
      store => store.handle === this.els.select.element.value,
    )
  }

  get restOfWorldStore () {
    return this.stores.find(store => store.isRestOfWorld)
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector, { extends: 'aside' })
}
